import classNames from 'classnames';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import styles from './loader.module.scss';

interface LoaderSharedProps {
  size?: 'tiny' | 'small' | 'medium' | 'large';
  fullSize?: boolean;
  className?: string;
}

interface LoaderProps extends LoaderSharedProps {
  type?: 'default' | 'spinner';
}

export const Loader = ({ type, ...props }: LoaderProps) => {
  switch (type) {
    case 'spinner':
      return <SpinnerLoader {...props} />;
    default:
      return <BarsLoader {...props} />;
  }
};

const BarsLoader = (props: LoaderSharedProps) => (
  <div
    className={classNames(
      {
        [styles.fullSize]: props.fullSize,
      },
      props.className,
    )}>
    <div
      className={classNames(styles.barsLoader, {
        [styles.large]: props.size === 'large',
        [styles.medium]: props.size === 'medium',
        [styles.small]: props.size === undefined || props.size === 'small',
      })}>
      <span />
      <span />
      <span />
    </div>
  </div>
);

const SpinnerLoader = (props: LoaderSharedProps) => (
  <div
    className={classNames(styles.spinnerLoader, props.className, {
      [styles.large]: props.size === 'large',
      [styles.medium]: props.size === 'medium',
      [styles.small]: props.size === undefined || props.size === 'small',
      [styles.tiny]: props.size === 'tiny',
    })}
  />
);

const InitialAppLoaderView = () => (
  <div className={styles.fullPage} role="status">
    <div className={styles.fullPageContent}>
      <img className={styles.fullPageMark} src="/logo-pinguin.svg" alt="" />
      <span className={styles.fullPageLabel}>Getting things ready…</span>
      <span className={styles.fullPageTrack} aria-hidden>
        <span className={styles.fullPageSweep} />
      </span>
    </div>
  </div>
);

const FullPageSpinnerView = () => (
  <div className={styles.fullPage} role="status" aria-label="Loading">
    <Loader type="spinner" size="medium" />
  </div>
);

type RegisterFullPageLoader = () => () => void;

const FullPageLoaderContext = createContext<RegisterFullPageLoader | null>(null);
const FullPageLoaderHandoffDelayMs = 100;

export const FullPageLoaderProvider = ({ children }: { children: React.ReactNode }) => {
  const activeLoaderCount = useRef(0);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasCompletedInitialLoad, setHasCompletedInitialLoad] = useState(false);

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current !== null) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const register = useCallback(() => {
    activeLoaderCount.current += 1;
    clearHideTimer();
    setIsVisible(true);

    return () => {
      activeLoaderCount.current = Math.max(0, activeLoaderCount.current - 1);

      if (activeLoaderCount.current > 0) {
        return;
      }

      clearHideTimer();
      hideTimer.current = setTimeout(() => {
        hideTimer.current = null;
        if (activeLoaderCount.current === 0) {
          setIsVisible(false);
          setHasCompletedInitialLoad(true);
        }
      }, FullPageLoaderHandoffDelayMs);
    };
  }, [clearHideTimer]);

  useEffect(() => () => clearHideTimer(), [clearHideTimer]);

  return (
    <FullPageLoaderContext.Provider value={register}>
      {children}
      {isVisible && (hasCompletedInitialLoad ? <FullPageSpinnerView /> : <InitialAppLoaderView />)}
    </FullPageLoaderContext.Provider>
  );
};

export const FullPageLoader = () => {
  const register = useContext(FullPageLoaderContext);

  useLayoutEffect(() => {
    if (register === null) {
      return;
    }

    return register();
  }, [register]);

  return register === null ? <InitialAppLoaderView /> : null;
};
